/*
 * Copyright (C) 2026 - present by Potentially
 *
 * Please see distribution for license.
 */

export const DEFAULT_BRAND_COLOUR = '#4338CA';

export const HEX_COLOUR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

function parseHex(hex: string): Rgb | null {
  if (!hex || !HEX_COLOUR_PATTERN.test(hex)) {
    return null;
  }
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function mix(colour: Rgb, target: Rgb, weight: number): Rgb {
  return {
    r: colour.r + (target.r - colour.r) * weight,
    g: colour.g + (target.g - colour.g) * weight,
    b: colour.b + (target.b - colour.b) * weight,
  };
}

export function deriveAccentFamily(brand: string): Record<string, string> {
  const base = parseHex(brand);
  if (!base) {
    return {};
  }
  return {
    '--accentSoft': toHex(mix(base, WHITE, 0.88)),
    '--accentBdr': toHex(mix(base, WHITE, 0.62)),
    '--accentMid': toHex(mix(base, WHITE, 0.12)),
    '--accent': toHex(base),
    '--accentDark': toHex(mix(base, BLACK, 0.32)),
  };
}
